import { useCallback, useMemo } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import { DASHBOARD_COLORS, COMMON_STYLES } from './styles';
import DataRow from './shared/DataRow';
import DashboardRoleSelector from './dashboard_selectors/DashboardRoleSelector';
import DashboardGroupSelector from './dashboard_selectors/DashboardGroupSelector';
import TierSelector from '@/components/selectors/TierSelector';
import { useSetUserTier } from '@/hooks/useSetUserTier';
import { getUserAssignedTier } from '@/utils/organization/getUserAssignedTier';
import { getIsOrgOnTrial } from '@/utils/organization/getIsOrgOnTrial';
import { buildPoolCounts, hasAvailableSeat } from '@/utils/organization/orgSeatAvailability';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import { isAdmin } from '@/components/routing/UserRoutes/AcceptedRoles';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import Group from '@/models/Organization/Group';
import { OrgUser } from '@/models/User/OrgUser';
import {
  LocationPreference,
  OrganizationOnAzure,
  SubscriptionTier,
} from '../../../../../../generated-client/models';
import { UserInfo } from '../UserDashboard';

const DASHBOARD_TRIAL_INFO_ICON_SIZE = 20;

interface UserDataSectionProps {
  userInfo: UserInfo | null;
  organization: OrganizationOnAzure;
  groups: Group[];
}

const UserDataSection = ({
  userInfo,
  organization,
  groups,
}: UserDataSectionProps) => {
  const { t } = useTranslation();
  const { orgUser } = useUserUpdaterProvider();
  const { updateOrganization } = useOrganizationUpdaterProvider();
  const viewerIsAdmin = isAdmin(orgUser?.role);
  const isOrgOnTrial = getIsOrgOnTrial(organization.trial_end_timestamp);

  const orgGroups = useMemo(
    () => Object.values(organization.groups ?? {}),
    [organization],
  );

  const fullPools = useMemo(
    () => {
      const poolCounts = buildPoolCounts(organization);
      return [SubscriptionTier.Ops, SubscriptionTier.Respond].filter(
        (tier) => !hasAvailableSeat(poolCounts, tier),
      );
    },
    [organization],
  );

  const updateAssignedTierLocally = useCallback(
    (tier: SubscriptionTier, user: { auth_id: string }, changedGroupName: string) => {
      const orgGroupMap = organization.groups ?? {};
      const currentGroup = orgGroupMap[changedGroupName];
      if (!currentGroup) return;

      const withoutUser = (ids?: string[]) =>
        (ids ?? []).filter((id) => id !== user.auth_id);

      const nextGroup = {
        ...currentGroup,
        connect_user_auth_ids: withoutUser(currentGroup.connect_user_auth_ids),
        ops_user_auth_ids: withoutUser(currentGroup.ops_user_auth_ids),
        respond_user_auth_ids: withoutUser(currentGroup.respond_user_auth_ids),
      };

      if (tier === SubscriptionTier.Ops) {
        nextGroup.ops_user_auth_ids = [
          ...nextGroup.ops_user_auth_ids,
          user.auth_id,
        ];
      } else if (tier === SubscriptionTier.Respond) {
        nextGroup.respond_user_auth_ids = [
          ...nextGroup.respond_user_auth_ids,
          user.auth_id,
        ];
      } else {
        nextGroup.connect_user_auth_ids = [
          ...nextGroup.connect_user_auth_ids,
          user.auth_id,
        ];
      }

      updateOrganization({
        ...organization,
        groups: {
          ...orgGroupMap,
          [changedGroupName]: nextGroup,
        },
      });
    },
    [organization, updateOrganization],
  );

  const { changeUserTier, inProgress } = useSetUserTier({
    updateLocalState: updateAssignedTierLocally,
  });

  const roleSelectorUser = {
    ...userInfo,
    auth_id: userInfo?.userAuthId,
    org_user_azure_id: userInfo?.orgUserAzureId,
    first_name: userInfo?.firstName,
    last_name: userInfo?.lastName,
    role: userInfo?.role,
  };

  const groupSelectorUser = new OrgUser({
    first_name: userInfo?.firstName ?? '',
    last_name: userInfo?.lastName ?? '',
    auth_id: userInfo?.userAuthId ?? '',
    id: userInfo?.orgUserAzureId ?? '',
    language_code: userInfo?.languageCode ?? 'en',
    location_preference:
      userInfo?.locationPreference ?? LocationPreference.EmergencyOnly,
    phone_number: userInfo?.phoneNumber ?? '',
    org_user_azure_id: userInfo?.orgUserAzureId ?? '',
    role: userInfo?.role ?? 'Mobile User',
    org_code: organization.org_code,
    last_interaction_timestamp: null,
    object_type: '',
    unique_key: `org_user_${userInfo?.userAuthId}`,
    unified_config_id: '',
    pvd_config_id: userInfo?.pvdConfigId ?? '',
    emergency_config_id: userInfo?.emergencyConfigId ?? '',
    should_force_apply_config: userInfo?.shouldForceApplyConfig ?? false,
    should_force_apply_pvd_config: false,
    should_force_apply_emergency_config: false,
    is_prototyper: false,
  });

  const roleList = [
    ...Object.keys(organization?.roles_with_features_default || {}),
  ];

  if (!userInfo) {
    return (
      <Paper
        variant="outlined"
        sx={{
          ...COMMON_STYLES.sectionPaper,
          flex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
            {t(StringKeys.USER_DATA)}
          </Typography>
          <Tooltip
            title={t(
              StringKeys.YOU_CAN_CHANGE_THESE_SETTINGS_IN_USER_MANAGEMENT_PAGE,
            )}
            placement="bottom-start"
            arrow
          >
            <InfoOutlined sx={{ color: DASHBOARD_COLORS.primary }} fontSize="small" />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <DataRow label={t(StringKeys.ROLE)}>
            <Typography>No User Data Available</Typography>
          </DataRow>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      // @ts-ignore
      variant="contained"
      sx={{
        ...COMMON_STYLES.sectionPaper,
        flex: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
          {t(StringKeys.USER_DATA)}
        </Typography>
        <Tooltip
          title={t(
            StringKeys.YOU_CAN_CHANGE_THESE_SETTINGS_IN_USER_MANAGEMENT_PAGE,
          )}
          placement="bottom-start"
          arrow
        >
          <InfoOutlined sx={{ color: DASHBOARD_COLORS.primary }} fontSize="small" />
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DataRow label={t(StringKeys.ROLE)}>
          <DashboardRoleSelector
            user={roleSelectorUser}
            roleList={roleList}
            orgAzureId={organization?.id ?? ''}
            orgCode={organization?.org_code ?? ''}
          />
        </DataRow>

        <DataRow label={t(StringKeys.GROUP)}>
          <DashboardGroupSelector
            allGroups={groups}
            currentGroup={userInfo?.groupName ?? 'Default'}
            user={groupSelectorUser}
            orgAzureId={organization?.id}
            orgCode={organization?.org_code}
          />
        </DataRow>

        <DataRow label={t(StringKeys.SUBSCRIPTION_STATUS)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {viewerIsAdmin ? (
              <TierSelector
                value={getUserAssignedTier(orgGroups, userInfo.userAuthId)}
                fullPools={fullPools}
                disabled={!viewerIsAdmin || inProgress}
                showTrialElevationInfo={isOrgOnTrial}
                trialInfoSide="left"
                trialInfoIconSize={DASHBOARD_TRIAL_INFO_ICON_SIZE}
                onChange={(tier) =>
                  changeUserTier(
                    tier,
                    {
                      auth_id: userInfo.userAuthId,
                      first_name: userInfo.firstName,
                      last_name: userInfo.lastName,
                    },
                    userInfo.groupName,
                  )
                }
              />
            ) : (
              <Typography sx={{ fontSize: 15 }}>
                {t(
                  getTierLabelKey(
                    getUserAssignedTier(orgGroups, userInfo.userAuthId),
                  ),
                )}
              </Typography>
            )}
          </Box>
        </DataRow>
      </Box>
    </Paper>
  );
};

export default UserDataSection;
